import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DEFAULT_FILTER,
  DISC_FORMATS,
  GENRES,
  SORT_KEYS,
  activeFilterCount,
  discCount,
  filterAndSortMovies,
  genreName,
  loanedCopies,
  recommend,
  surpriseMe,
  type CollectionFilter,
  type DiscFormat,
  type LoanState,
  type SortDirection,
  type MessageKey,
  type SortKey,
  type WatchState,
} from '@physical-movies/core';
import { useCollection, useI18n } from '../app/context';
import { PageHeader } from '../components/Page';
import { PosterGrid } from '../components/Poster';
import { RecommendationCard } from '../components/Recommendation';
import { Sheet } from '../components/Sheet';
import { Button, Chip, EmptyState, IconButton, SearchInput, Segmented } from '../components/ui';
import { readStoredFilter, writeStoredFilter } from '../lib/filterStorage';
import { useFormatLabels } from '../lib/labels';
import { useScrollRestoration } from '../lib/useScrollRestoration';
import styles from './collection.module.css';

/**
 * Keyed by SortKey, so adding a sort to the core list fails the build here
 * until it has a label — a local copy of the key list silently dropped
 * "release date" off the sheet once already.
 */
const SORT_LABEL: Record<SortKey, MessageKey> = {
  title: 'sort.title',
  added: 'sort.added',
  releaseDate: 'sort.releaseDate',
  lastWatched: 'sort.lastWatched',
  watchCount: 'sort.watchCount',
  runtime: 'sort.runtime',
};

export function Collection() {
  const { t, locale } = useI18n();
  const { movies, watches, settings, saveSettings } = useCollection();
  const navigate = useNavigate();
  const formatLabels = useFormatLabels();

  // Seeded from the tab's remembered filter so that opening a movie and coming
  // back does not silently reset it.
  const [localFilter, setLocalFilter] = useState<CollectionFilter>(readStoredFilter);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [surprise, setSurprise] = useState<ReturnType<typeof surpriseMe>>(null);
  const [surpriseOpen, setSurpriseOpen] = useState(false);

  /**
   * Filters are per tab; the sort is not.
   *
   * Which films you are looking at right now is a passing thought, but the
   * order you like your shelf in is a preference — so the sort comes from the
   * settings document and follows you to any device, while the rest stays in
   * session storage. Composing them here means there is exactly one filter
   * object downstream and no second copy of the sort to fall out of step.
   */
  const filter = useMemo<CollectionFilter>(
    () => ({
      ...localFilter,
      sort: settings.collectionSort.key,
      direction: settings.collectionSort.direction,
    }),
    [localFilter, settings.collectionSort],
  );

  useEffect(() => {
    writeStoredFilter(filter);
  }, [filter]);

  useScrollRestoration('collection');

  const visible = useMemo(
    () => filterAndSortMovies(movies, filter, locale, settings),
    [movies, filter, locale, settings],
  );

  const totalDiscs = useMemo(
    () => movies.reduce((sum, movie) => sum + discCount(movie), 0),
    [movies],
  );

  const lentOutCount = useMemo(
    () => movies.reduce((sum, movie) => sum + loanedCopies(movie).length, 0),
    [movies],
  );

  // Only offer genres that are actually on the shelf.
  const availableGenres = useMemo(() => {
    const present = new Set<number>();
    for (const movie of movies) for (const id of movie.genreIds) present.add(id);
    return [...present]
      .filter((id) => id in GENRES)
      .sort((a, b) => (genreName(a, locale) ?? '').localeCompare(genreName(b, locale) ?? ''));
  }, [movies, locale]);

  const activeCount = activeFilterCount(filter);
  const patch = (next: Partial<CollectionFilter>) =>
    setLocalFilter((current) => ({ ...current, ...next }));

  const setSort = (key: SortKey) =>
    void saveSettings({ ...settings, collectionSort: { ...settings.collectionSort, key } });
  const setDirection = (direction: SortDirection) =>
    void saveSettings({ ...settings, collectionSort: { ...settings.collectionSort, direction } });

  const toggleLocation = (id: string) =>
    patch({
      locationIds: filter.locationIds.includes(id)
        ? filter.locationIds.filter((entry) => entry !== id)
        : [...filter.locationIds, id],
    });

  const toggleFormat = (format: DiscFormat) =>
    patch({
      formats: filter.formats.includes(format)
        ? filter.formats.filter((entry) => entry !== format)
        : [...filter.formats, format],
    });

  const toggleGenre = (id: number) =>
    patch({
      genreIds: filter.genreIds.includes(id)
        ? filter.genreIds.filter((entry) => entry !== id)
        : [...filter.genreIds, id],
    });

  const rollDice = () => {
    const picks = recommend(movies, watches, { limit: 40 });
    setSurprise(surpriseMe(picks));
    setSurpriseOpen(true);
  };

  if (movies.length === 0) {
    return (
      <>
        <PageHeader title={t('collection.title')} />
        <EmptyState
          icon="disc"
          title={t('collection.empty.title')}
          body={t('collection.empty.body')}
          action={
            <Button variant="primary" onClick={() => void navigate('/add')}>
              {t('collection.empty.action')}
            </Button>
          }
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('collection.title')}
        subtitle={t('collection.summary', { count: movies.length, discs: totalDiscs })}
        actions={
          <>
            <IconButton name="dice" label={t('collection.surprise')} onClick={rollDice} />
            <IconButton
              name="print"
              label={t('collection.print')}
              onClick={() => void navigate('/print')}
            />
            <IconButton
              name="sliders"
              label={
                activeCount > 0
                  ? t('collection.filtersActive', { count: activeCount })
                  : t('collection.filters')
              }
              variant={activeCount > 0 ? 'secondary' : 'ghost'}
              onClick={() => setFiltersOpen(true)}
            />
          </>
        }
      />

      <div className={styles.toolbar}>
        <SearchInput
          value={filter.query}
          onValueChange={(query) => patch({ query })}
          placeholder={t('collection.searchPlaceholder')}
          label={t('collection.searchLabel')}
          clearLabel={t('common.clear')}
        />
        <div className={styles.chips}>
          {DISC_FORMATS.map((format) => (
            <Chip
              key={format}
              selected={filter.formats.includes(format)}
              onClick={() => toggleFormat(format)}
            >
              {formatLabels.long(format)}
            </Chip>
          ))}
          <Chip
            selected={filter.watchState === 'unwatched'}
            onClick={() =>
              patch({ watchState: filter.watchState === 'unwatched' ? 'all' : 'unwatched' })
            }
          >
            {t('filter.unwatched')}
          </Chip>
          {lentOutCount > 0 && (
            <Chip
              selected={filter.loanState === 'loaned'}
              count={lentOutCount}
              onClick={() => patch({ loanState: filter.loanState === 'loaned' ? 'all' : 'loaned' })}
            >
              {t('filter.loaned')}
            </Chip>
          )}
        </div>
      </div>

      {(filter.query || activeCount > 0) && (
        <p className={styles.results}>{t('collection.results', { count: visible.length })}</p>
      )}

      {visible.length === 0 ? (
        <EmptyState
          icon="search"
          title={t('collection.noResults.title')}
          body={t('collection.noResults.body')}
          action={
            <Button onClick={() => setLocalFilter(DEFAULT_FILTER)}>
              {t('collection.clearFilters')}
            </Button>
          }
        />
      ) : (
        <PosterGrid movies={visible} />
      )}

      <Sheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title={t('filter.title')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button onClick={() => setLocalFilter({ ...DEFAULT_FILTER, query: filter.query })}>
              {t('common.reset')}
            </Button>
            <Button variant="primary" onClick={() => setFiltersOpen(false)}>
              {t('collection.results', { count: visible.length })}
            </Button>
          </>
        }
      >
        <div className={styles.filterGroup}>
          <span className={`eyebrow ${styles.filterGroupTitle}`}>{t('filter.format')}</span>
          <div className={styles.filterOptions}>
            {DISC_FORMATS.map((format) => (
              <Chip
                key={format}
                selected={filter.formats.includes(format)}
                onClick={() => toggleFormat(format)}
              >
                {formatLabels.long(format)}
              </Chip>
            ))}
          </div>
        </div>

        <div className={styles.filterGroup}>
          <span className={`eyebrow ${styles.filterGroupTitle}`}>{t('filter.watchState')}</span>
          <Segmented<WatchState>
            label={t('filter.watchState')}
            value={filter.watchState}
            onChange={(watchState) => patch({ watchState })}
            options={[
              { value: 'all', label: t('filter.any') },
              { value: 'watched', label: t('filter.watched') },
              { value: 'unwatched', label: t('filter.unwatched') },
            ]}
          />
        </div>

        <div className={styles.filterGroup}>
          <span className={`eyebrow ${styles.filterGroupTitle}`}>{t('filter.loanState')}</span>
          <Segmented<LoanState>
            label={t('filter.loanState')}
            value={filter.loanState}
            onChange={(loanState) => patch({ loanState })}
            options={[
              { value: 'all', label: t('filter.any') },
              { value: 'shelf', label: t('filter.onShelf') },
              { value: 'loaned', label: t('filter.loaned') },
            ]}
          />
        </div>

        {settings.locations.length > 0 && (
          <div className={styles.filterGroup}>
            <span className={`eyebrow ${styles.filterGroupTitle}`}>{t('filter.location')}</span>
            <div className={styles.filterOptions}>
              {settings.locations.map((location) => (
                <Chip
                  key={location.id}
                  selected={filter.locationIds.includes(location.id)}
                  onClick={() => toggleLocation(location.id)}
                >
                  {location.name}
                </Chip>
              ))}
            </div>
          </div>
        )}

        {availableGenres.length > 0 && (
          <div className={styles.filterGroup}>
            <span className={`eyebrow ${styles.filterGroupTitle}`}>{t('filter.genres')}</span>
            <div className={styles.filterOptions}>
              {availableGenres.map((id) => (
                <Chip
                  key={id}
                  selected={filter.genreIds.includes(id)}
                  onClick={() => toggleGenre(id)}
                >
                  {genreName(id, locale)}
                </Chip>
              ))}
            </div>
          </div>
        )}

        <div className={styles.filterGroup}>
          <span className={`eyebrow ${styles.filterGroupTitle}`}>{t('collection.sort')}</span>
          <div className={styles.sortRow}>
            <div className={styles.filterOptions}>
              {SORT_KEYS.map((key) => (
                <Chip key={key} selected={filter.sort === key} onClick={() => setSort(key)}>
                  {t(SORT_LABEL[key])}
                </Chip>
              ))}
            </div>
            <Segmented<SortDirection>
              label={t('collection.sort')}
              value={filter.direction}
              onChange={setDirection}
              options={[
                { value: 'asc', label: t('sort.ascending') },
                { value: 'desc', label: t('sort.descending') },
              ]}
            />
          </div>
        </div>
      </Sheet>

      <Sheet
        open={surpriseOpen}
        onClose={() => setSurpriseOpen(false)}
        title={t('rec.title')}
        closeLabel={t('common.close')}
        footer={surprise ? <Button onClick={rollDice}>{t('rec.another')}</Button> : undefined}
      >
        <div className={styles.surpriseBody}>
          {surprise ? (
            <RecommendationCard
              recommendation={surprise}
              onNavigate={() => setSurpriseOpen(false)}
            />
          ) : (
            <EmptyState icon="check" title={t('rec.empty.title')} body={t('rec.empty.body')} />
          )}
        </div>
      </Sheet>
    </>
  );
}
